/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CancellationReasonProps {
  id: string;
  name: string;
  abbreviation: string;
  canBeUnitCancellationReason: boolean | null;
  isTransferCenterReason: boolean;
  cancellationCategoryId: string | null;
  secondaryReasonRequired: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CancellationReasonRecord extends Record<CancellationReasonProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  canBeUnitCancellationReason: null as any,
  isTransferCenterReason: null as any,
  cancellationCategoryId: null as any,
  secondaryReasonRequired: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    canBeUnitCancellationReason: boolean | null;
    isTransferCenterReason: boolean;
    cancellationCategoryId: string | null;
    secondaryReasonRequired: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CancellationReasonRecord {
    if (json == null) {
      throw new Error('CancellationReasonRecord.fromJS: parameter cannot be null');
    }
    return new CancellationReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CancellationReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'CancellationReasonJson.name'),
      abbreviation: getFromOrThrow(
        json,
        (j) => j.abbreviation,
        'CancellationReasonJson.abbreviation',
      ),
      canBeUnitCancellationReason: json.canBeUnitCancellationReason,
      isTransferCenterReason: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isTransferCenterReason', false),
        'CancellationReasonJson.isTransferCenterReason',
      ),
      cancellationCategoryId: json.cancellationCategoryId,
      secondaryReasonRequired: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'secondaryReasonRequired', false),
        'CancellationReasonJson.secondaryReasonRequired',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    canBeUnitCancellationReason: boolean | null;
    isTransferCenterReason: boolean;
    cancellationCategoryId: string | null;
    secondaryReasonRequired: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CancellationReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('CancellationReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CancellationReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CancellationReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'CancellationReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'CancellationReasonJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      canBeUnitCancellationReason: tryOrDefault(
        () => json.canBeUnitCancellationReason,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isTransferCenterReason: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isTransferCenterReason', false),
            'CancellationReasonJson.isTransferCenterReason',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationCategoryId: tryOrDefault(
        () => json.cancellationCategoryId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      secondaryReasonRequired: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'secondaryReasonRequired', false),
            'CancellationReasonJson.secondaryReasonRequired',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CancellationReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('CancellationReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CancellationReasonViewModelBase implements CancellationReasonProps {
  protected readonly _props: CancellationReasonProps;
  private _json?: CancellationReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get canBeUnitCancellationReason(): boolean | null {
    return this._props.canBeUnitCancellationReason;
  }
  get isTransferCenterReason(): boolean {
    return this._props.isTransferCenterReason;
  }
  get cancellationCategoryId(): string | null {
    return this._props.cancellationCategoryId;
  }
  get secondaryReasonRequired(): boolean {
    return this._props.secondaryReasonRequired;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CancellationReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        canBeUnitCancellationReason: this._props.canBeUnitCancellationReason,
        isTransferCenterReason: this._props.isTransferCenterReason,
        cancellationCategoryId: this._props.cancellationCategoryId,
        secondaryReasonRequired: this._props.secondaryReasonRequired,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCancellationReasonEntities = (state: AppState) =>
  state.entityStores.cancellationReason.entities;

export const selectCancellationReasonById = (id: string) => (state: AppState) =>
  state.entityStores.cancellationReason.entities.get(id);

export const selectCancellationReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cancellationReason.meta.get(id);

export const selectCancellationReasonAndMetadataById = (id: string) =>
  createSelector(
    selectCancellationReasonById(id),
    selectCancellationReasonMetadataById(id),
    (cancellationReason, meta) => ({ cancellationReason, meta }),
  );

export function selectCancellationReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCancellationReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CancellationReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCancellationReasonEntitiesByName(name: string) {
  return createSelector(selectAllCancellationReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectCancellationReasonEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllCancellationReasonEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectCancellationReasonEntitiesByCanBeUnitCancellationReason(
  canBeUnitCancellationReason: boolean,
) {
  return createSelector(selectAllCancellationReasonEntities, (entities) =>
    entities
      .filter((e) => e.canBeUnitCancellationReason === canBeUnitCancellationReason)
      .valueSeq(),
  );
}

export function selectCancellationReasonEntitiesByIsTransferCenterReason(
  isTransferCenterReason: boolean,
) {
  return createSelector(selectAllCancellationReasonEntities, (entities) =>
    entities.filter((e) => e.isTransferCenterReason === isTransferCenterReason).valueSeq(),
  );
}

export function selectCancellationReasonEntitiesByCancellationCategoryId(
  cancellationCategoryId: string,
) {
  return createSelector(selectAllCancellationReasonEntities, (entities) =>
    entities.filter((e) => e.cancellationCategoryId === cancellationCategoryId).valueSeq(),
  );
}

export function selectCancellationReasonEntitiesBySecondaryReasonRequired(
  secondaryReasonRequired: boolean,
) {
  return createSelector(selectAllCancellationReasonEntities, (entities) =>
    entities.filter((e) => e.secondaryReasonRequired === secondaryReasonRequired).valueSeq(),
  );
}
//#endregion Selectors
